import { useEffect, useMemo, useState } from 'react';
import { Callout, SelectField as MangoSelect, Spinner, Tab, TabGroup, Table, Tag, type SelectFieldOption, type TableColumn } from '@humanforce/mango-ui-react';
import type { RegionDto, RegionsClient } from '@pay-roadrunner/regions-ui';
import { isTaxConfigurationUnauthorized, type TaxConfigurationClient } from './taxConfigurationHttpClient';
import type {
  TaxConfigurationGroupDetailResponse,
  TaxFinancialYearDto,
} from './taxConfigurationApiTypes';
import {
  buildTaxConfigurationSections,
  buildTaxScaleModel,
  type TaxConfigurationFact,
  type TaxConfigurationSection,
  type TaxConfigurationSectionRow,
  type TaxScaleBracketRow,
} from './taxConfigurationModel';

const GROUPS = [
  { key: 'payg-scales', label: 'PAYG scales' },
  { key: 'medicare-stsl', label: 'Medicare & STSL' },
  { key: 'offsets', label: 'Offsets' },
  { key: 'termination', label: 'Termination' },
  { key: 'age-rules', label: 'Age rules' },
  { key: 'adjustments', label: 'Adjustments' },
] as const;

type LoadState = 'loading' | 'ready' | 'empty' | 'error' | 'unauthorized';
type GroupKey = (typeof GROUPS)[number]['key'];

const GROUP_KEYS = GROUPS.map((group) => group.key);
export const isTaxConfigurationGroupKey = (value: string): value is GroupKey => GROUP_KEYS.includes(value as GroupKey);

const baseBracketColumns: TableColumn[] = [
  { field: 'earningsFrom', header: 'Weekly earnings from' },
  { field: 'earningsTo', header: 'To less than' },
  { field: 'coefficientA', header: 'A coefficient' },
  { field: 'coefficientB', header: 'B coefficient' },
];

const LoadingState = ({ label }: { label: string }) => (
  <div className="mgo-cluster mgo-gap-s mgo-align-center" aria-busy="true" aria-live="polite">
    <Spinner />
    <span className="mgo-text-s mgo-color-muted">{label}</span>
  </div>
);

const SelectField = ({
  disabled,
  label,
  onChange,
  options,
  placeholder,
  value,
}: {
  disabled?: boolean;
  label: string;
  onChange: (value: string) => void;
  options: SelectFieldOption[];
  placeholder?: string;
  value: string;
}) => {
  const id = `tax-configuration-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <div className="mgo-stack mgo-gap-xs">
      <MangoSelect
        id={id}
        label={label}
        value={value}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(next) => onChange(String(next ?? ''))}
      />
    </div>
  );
};

const StatusTag = ({ status }: { status: string }) => {
  const normalized = status.toLowerCase();
  const tone = normalized === 'published' ? 'positive' : normalized === 'imported' || normalized === 'draft' ? 'caution' : 'neutral';
  return <Tag label={status || 'Not returned'} tone={tone} />;
};

const panelStyle = {
  background: 'var(--mgo-color-bg-raised)',
  border: 'var(--mgo-border-neutral)',
  borderRadius: 'var(--mgo-radius)',
  padding: 'var(--mgo-space-40)',
} as const;

const selectorItemStyle = (selected: boolean) => ({
  background: selected ? 'var(--mgo-color-bg-selected, var(--mgo-color-bg-brand-soft-bordered))' : 'var(--mgo-color-bg-raised)',
  border: selected ? 'var(--mgo-border-selected, var(--mgo-border-neutral))' : 'var(--mgo-border-neutral)',
  borderRadius: 'var(--mgo-radius)',
  color: 'var(--mgo-color-fg-neutral)',
  cursor: 'pointer',
  font: 'inherit',
  minHeight: '5.25rem',
  padding: 'var(--mgo-space-30)',
  textAlign: 'left' as const,
  width: '100%',
});

const factBoxStyle = {
  border: 'var(--mgo-border-neutral)',
  borderRadius: 'var(--mgo-radius)',
  minHeight: '4rem',
  padding: 'var(--mgo-space-30)',
} as const;

const splitLayoutStyle = {
  alignItems: 'start',
  gridTemplateColumns: 'minmax(17.5rem, 19rem) minmax(0, 1fr)',
} as const;

const rowCountLabel = (count: number) => `${count} row${count === 1 ? '' : 's'}`;

const taxScaleHeading = (scaleCode: string, displayName: string) => {
  const prefix = `Scale ${scaleCode}`;
  return displayName.toLowerCase().startsWith(prefix.toLowerCase()) ? displayName : `${prefix} - ${displayName}`;
};

const FactGrid = ({ facts }: { facts: TaxConfigurationFact[] }) => (
  <dl className="mgo-grid mgo-grid-auto-fit-m mgo-gap-m">
    {facts.map((fact) => (
      <div key={fact.label} style={factBoxStyle}>
        <dt className="mgo-text-xs mgo-color-muted">{fact.label}</dt>
        <dd className="mgo-text-s mgo-weight-strong" style={{ margin: 0, overflowWrap: 'anywhere' }}>{fact.value}</dd>
      </div>
    ))}
  </dl>
);

const SelectorPanel = ({
  items,
  onSelect,
  selectedKey,
}: {
  items: { key: string; title: string; subtitle: string; rowCount: number }[];
  onSelect: (key: string) => void;
  selectedKey: string;
}) => (
  <aside className="mgo-stack mgo-gap-s" aria-label="Selector" style={panelStyle}>
    <div className="mgo-text-xs mgo-weight-strong mgo-color-muted" style={{ textTransform: 'uppercase' }}>Selector</div>
    {items.map((item) => (
      <button
        key={item.key}
        type="button"
        aria-current={item.key === selectedKey ? 'true' : undefined}
        className="mgo-stack mgo-gap-xs"
        style={selectorItemStyle(item.key === selectedKey)}
        onClick={() => onSelect(item.key)}
      >
        <span className="mgo-text-s mgo-weight-strong">{item.title}</span>
        <span className="mgo-text-s mgo-color-muted">{item.subtitle}</span>
        <span style={{ alignSelf: 'flex-start' }}>
          <Tag label={rowCountLabel(item.rowCount)} tone="neutral" />
        </span>
      </button>
    ))}
  </aside>
);

const TaxScaleMasterDetail = ({
  groupDetails,
  groupMessage,
  groupState,
}: {
  groupDetails: TaxConfigurationGroupDetailResponse | null;
  groupMessage: string;
  groupState: LoadState;
}) => {
  const [selectedScaleCode, setSelectedScaleCode] = useState('');
  const { bracketsByScaleCode, scales } = useMemo(() => buildTaxScaleModel(groupDetails), [groupDetails]);

  useEffect(() => {
    setSelectedScaleCode((current) => {
      if (!scales.length) return '';
      return current && scales.some((scale) => scale.scaleCode === current) ? current : scales[0].scaleCode;
    });
  }, [scales]);

  const selectedScale = scales.find((scale) => scale.scaleCode === selectedScaleCode);
  const selectedBrackets = selectedScale ? bracketsByScaleCode.get(selectedScale.scaleCode) ?? [] : [];
  const selectedBracketColumns = useMemo(() => [
    ...baseBracketColumns,
    ...(selectedBrackets.some((row) => row.hasRate) ? [{ field: 'rate', header: 'Rate' }] : []),
    { field: 'isStepped', header: 'Stepped' },
  ], [selectedBrackets]);

  if (groupState === 'loading') return <LoadingState label="Loading Tax Scales data..." />;

  return (
    <section className="mgo-stack mgo-gap-l" aria-label="Tax scales">
      {groupState === 'error' && (
        <Callout tone="critical" title="Tax Configuration unavailable">
          {groupMessage || 'Failed to load Tax Configuration group data.'}
        </Callout>
      )}
      {groupState === 'empty' && (
        <Callout tone="neutral" title="No tax scales">
          {groupMessage || 'No tax scales returned for this selection.'}
        </Callout>
      )}

      {groupState === 'ready' && !scales.length && (
        <Callout tone="neutral" title="No tax scales">
          No TaxScaleCatalogue rows returned for this selection.
        </Callout>
      )}

      {groupState === 'ready' && scales.length > 0 && (
        <div className="mgo-grid mgo-gap-l" style={splitLayoutStyle}>
          <SelectorPanel
            selectedKey={selectedScaleCode}
            onSelect={setSelectedScaleCode}
            items={scales.map((scale) => ({
              key: scale.scaleCode,
              title: `Scale ${scale.scaleCode}`,
              subtitle: scale.displayName,
              rowCount: bracketsByScaleCode.get(scale.scaleCode)?.length ?? 0,
            }))}
          />

          <div className="mgo-stack mgo-gap-l" style={panelStyle}>
            {selectedScale ? (
              <>
                <section className="mgo-stack mgo-gap-m" aria-label="Selected tax scale details">
                  <div className="mgo-split mgo-gap-l mgo-align-start">
                    <div className="mgo-stack mgo-gap-xs">
                      <h3 className="mgo-text-l mgo-weight-strong">{taxScaleHeading(selectedScale.scaleCode, selectedScale.displayName)}</h3>
                      <p className="mgo-text-s mgo-color-muted">{selectedScale.description}</p>
                    </div>
                    <div>
                      <StatusTag status={selectedScale.status} />
                    </div>
                  </div>

                  <FactGrid facts={[
                    { label: 'Effective from', value: selectedScale.effectiveFrom },
                    { label: 'Effective to', value: selectedScale.effectiveTo },
                    { label: 'Active', value: selectedScale.active },
                    { label: 'Source', value: selectedScale.source },
                    { label: 'Import run', value: selectedScale.importRun },
                  ]}
                  />
                </section>

                <section className="mgo-stack mgo-gap-m" aria-label="Bracket coefficients">
                  <h4 className="mgo-text-m mgo-weight-strong">Bracket coefficients</h4>
                  <Table<TaxScaleBracketRow>
                    value={selectedBrackets}
                    columns={selectedBracketColumns}
                    emptyMessage="No bracket rows returned for the selected scale."
                    scrollable
                    testId="tax-scale-bracket-table"
                  />
                </section>

              </>
            ) : (
              <Callout tone="neutral" title="No selected scale">
                No selected tax scale details are available.
              </Callout>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

const TaxConfigurationSectionDetail = ({ section }: { section: TaxConfigurationSection }) => (
  <main className="mgo-stack mgo-gap-l" style={panelStyle}>
    <section className="mgo-stack mgo-gap-m" aria-label={`${section.title} details`}>
      <div className="mgo-split mgo-gap-l mgo-align-start">
        <div className="mgo-stack mgo-gap-xs">
          <h3 className="mgo-text-l mgo-weight-strong">{section.title}</h3>
          <p className="mgo-text-s mgo-color-muted">{`${rowCountLabel(section.rowCount)} returned for this selection.`}</p>
        </div>
        <div>
          <StatusTag status={section.status} />
        </div>
      </div>
      <FactGrid facts={section.facts} />
    </section>

    {section.displayMode === 'fields' ? (
      <FactGrid
        facts={section.rows[0]
          ? section.columns.map((column) => ({ label: column.header, value: section.rows[0][column.field] ?? 'Not returned' }))
          : [{ label: section.title, value: section.emptyMessage }]}
      />
    ) : (
      <Table<TaxConfigurationSectionRow>
        value={section.rows}
        columns={section.columns}
        emptyMessage={section.emptyMessage}
        scrollable
        testId={`tax-configuration-${section.key}-table`}
      />
    )}
  </main>
);

// Exported so consuming apps can reuse it directly for other generic group
// keys (e.g. an "import-history" panel) without re-fetching through the full
// TaxConfigurationScreen bootstrap flow.
export const SectionedTaxConfigurationGroup = ({
  groupDetails,
  groupKey,
  groupLabel,
  groupMessage,
  groupState,
}: {
  groupDetails: TaxConfigurationGroupDetailResponse | null;
  groupKey: string;
  groupLabel: string;
  groupMessage: string;
  groupState: LoadState;
}) => {
  const sections = useMemo(() => buildTaxConfigurationSections(groupKey, groupDetails), [groupDetails, groupKey]);
  const [selectedSectionKey, setSelectedSectionKey] = useState('');

  useEffect(() => {
    setSelectedSectionKey((current) => {
      if (!sections.length) return '';
      return current && sections.some((section) => section.key === current) ? current : sections[0].key;
    });
  }, [sections]);

  const selectedSection = sections.find((section) => section.key === selectedSectionKey);

  if (groupState === 'loading') return <LoadingState label={`Loading ${groupLabel} data...`} />;

  return (
    <section className="mgo-stack mgo-gap-l" aria-label={groupLabel}>
      {groupState === 'error' && <Callout tone="critical" title="Tax Configuration unavailable">{groupMessage}</Callout>}
      {groupState === 'empty' && <Callout tone="neutral" title="No data">{groupMessage}</Callout>}
      {groupState === 'ready' && !sections.length && (
        <Callout tone="neutral" title="No data">
          No configured sections are available for this group.
        </Callout>
      )}
      {groupState === 'ready' && sections.length > 0 && (
        <div className="mgo-grid mgo-gap-l" style={splitLayoutStyle}>
          <SelectorPanel
            selectedKey={selectedSectionKey}
            onSelect={setSelectedSectionKey}
            items={sections.map((section) => ({
              key: section.key,
              title: section.title,
              subtitle: section.categoryLabel,
              rowCount: section.rowCount,
            }))}
          />
          {selectedSection ? (
            <TaxConfigurationSectionDetail section={selectedSection} />
          ) : (
            <Callout tone="neutral" title="No selected section">
              No selected Tax Configuration section details are available.
            </Callout>
          )}
        </div>
      )}
    </section>
  );
};

export interface TaxConfigurationScreenProps {
  regionsClient: RegionsClient;
  taxConfigurationClient: TaxConfigurationClient;
}

export const TaxConfigurationScreen = ({ regionsClient, taxConfigurationClient }: TaxConfigurationScreenProps) => {
  const [regions, setRegions] = useState<RegionDto[]>([]);
  const [financialYears, setFinancialYears] = useState<TaxFinancialYearDto[]>([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedFinancialYear, setSelectedFinancialYear] = useState('');
  const [activeGroup, setActiveGroup] = useState<GroupKey>('payg-scales');
  const [bootstrapState, setBootstrapState] = useState<LoadState>('loading');
  const [groupState, setGroupState] = useState<LoadState>('loading');
  const [bootstrapMessage, setBootstrapMessage] = useState('');
  const [groupMessage, setGroupMessage] = useState('');
  const [groupDetails, setGroupDetails] = useState<TaxConfigurationGroupDetailResponse | null>(null);

  useEffect(() => {
    let ignore = false;
    setBootstrapState('loading');
    setBootstrapMessage('');
    setGroupDetails(null);
    (async () => {
      try {
        const response = await regionsClient.fetchRegions();
        if (ignore) return;
        const activeRegions = (response.result ?? []).filter((region) => region.isActive);
        setRegions(activeRegions);
        setSelectedRegion(activeRegions[0]?.code ?? '');
        setBootstrapState(activeRegions.length ? 'ready' : 'empty');
        if (!activeRegions.length) setBootstrapMessage('No Tax Configuration regions are available.');
      } catch (error) {
        if (ignore) return;
        if (isTaxConfigurationUnauthorized(error)) {
          setBootstrapState('unauthorized');
          setBootstrapMessage('You are not authorised to view Tax Configuration data.');
        } else {
          setBootstrapState('error');
          setBootstrapMessage(error instanceof Error ? error.message : 'Failed to load Tax Configuration regions.');
        }
        setRegions([]);
        setFinancialYears([]);
        setSelectedRegion('');
        setSelectedFinancialYear('');
        setGroupDetails(null);
      }
    })();
    return () => { ignore = true; };
  }, [regionsClient]);

  useEffect(() => {
    if (!selectedRegion) {
      setGroupDetails(null);
      return;
    }
    let ignore = false;
    setBootstrapState('loading');
    setBootstrapMessage('');
    setFinancialYears([]);
    setSelectedFinancialYear('');
    setGroupState('loading');
    setGroupMessage('');
    setGroupDetails(null);
    (async () => {
      try {
        const response = await taxConfigurationClient.fetchTaxFinancialYears(selectedRegion);
        if (ignore) return;
        const years = response.result ?? [];
        setFinancialYears(years);
        setSelectedFinancialYear(years[0]?.financialYear ?? '');
        setBootstrapState(years.length ? 'ready' : 'empty');
        if (!years.length) setBootstrapMessage('No financial years returned.');
      } catch (error) {
        if (ignore) return;
        if (isTaxConfigurationUnauthorized(error)) {
          setBootstrapState('unauthorized');
          setBootstrapMessage('You are not authorised to view Tax Configuration data.');
        } else {
          setBootstrapState('error');
          setBootstrapMessage('Failed to load Tax Configuration financial years.');
        }
        setFinancialYears([]);
        setGroupDetails(null);
      }
    })();
    return () => { ignore = true; };
  }, [selectedRegion, taxConfigurationClient]);

  useEffect(() => {
    if (!selectedRegion || !selectedFinancialYear) {
      setGroupDetails(null);
      return;
    }
    let ignore = false;
    setGroupState('loading');
    setGroupMessage('');
    setGroupDetails(null);
    (async () => {
      try {
        const groupResponse = await taxConfigurationClient.fetchTaxConfigurationGroupDetails(activeGroup, selectedRegion, selectedFinancialYear);
        if (ignore) return;
        setGroupDetails(groupResponse.result ?? null);
        const rows = groupResponse.result?.rows ?? [];
        setGroupState(rows.length ? 'ready' : 'empty');
        if (!rows.length) setGroupMessage('No backend rows returned for this group.');
      } catch (error) {
        if (ignore) return;
        if (isTaxConfigurationUnauthorized(error)) {
          setGroupState('unauthorized');
          setGroupMessage('You are not authorised to view Tax Configuration data.');
        } else {
          setGroupState('error');
          setGroupMessage('Failed to load Tax Configuration group data.');
        }
        setGroupDetails(null);
      }
    })();
    return () => { ignore = true; };
  }, [activeGroup, selectedFinancialYear, selectedRegion, taxConfigurationClient]);

  const regionOptions = useMemo(
    () => regions.map((region) => ({ label: `${region.name} (${region.code})`, value: region.code })),
    [regions],
  );
  const yearOptions = useMemo(
    () => financialYears.map((year) => ({ label: year.financialYear, value: year.financialYear })),
    [financialYears],
  );
  const selectorsDisabled = bootstrapState === 'loading' || bootstrapState === 'unauthorized';
  const hasSelection = selectedRegion && selectedFinancialYear;
  const isUnauthorized = bootstrapState === 'unauthorized' || groupState === 'unauthorized';
  const unauthorizedMessage = bootstrapMessage || groupMessage || 'You are not authorised to view Tax Configuration data.';
  const clearLoadedTaxConfiguration = () => {
    setGroupDetails(null);
    setGroupMessage('');
    setGroupState('loading');
  };
  const handleRegionChange = (value: string) => {
    setSelectedRegion(value);
    setSelectedFinancialYear('');
    clearLoadedTaxConfiguration();
  };
  const handleFinancialYearChange = (value: string) => {
    setSelectedFinancialYear(value);
    clearLoadedTaxConfiguration();
  };
  const handleGroupChange = (value: string) => {
    if (isTaxConfigurationGroupKey(value)) setActiveGroup(value);
  };

  return (
    <section className="mgo-stack mgo-gap-l" aria-label="Tax Configuration">
      <div className="mgo-stack mgo-gap-xs">
        <h2 className="mgo-text-l mgo-weight-strong">Tax Configuration</h2>
        <p className="mgo-text-s mgo-color-muted">
          Read-only statutory configuration data from backend Tax Configuration APIs.
        </p>
      </div>

      <div className="mgo-grid mgo-grid-auto-fit-l mgo-gap-l">
        <SelectField
          label="Region"
          value={selectedRegion}
          options={regionOptions}
          placeholder="Select a region"
          disabled={selectorsDisabled || !regionOptions.length}
          onChange={handleRegionChange}
        />
        <SelectField
          label="Financial year"
          value={selectedFinancialYear}
          options={yearOptions}
          placeholder="Select a financial year"
          disabled={selectorsDisabled || !yearOptions.length}
          onChange={handleFinancialYearChange}
        />
      </div>

      {bootstrapState === 'loading' && <LoadingState label="Loading Tax Configuration data..." />}
      {isUnauthorized && (
        <Callout tone="critical" title="Unauthorised">
          {unauthorizedMessage}
        </Callout>
      )}
      {bootstrapState === 'error' && (
        <Callout tone="critical" title="Tax Configuration unavailable">
          {bootstrapMessage || 'Tax Configuration data could not be loaded.'}
        </Callout>
      )}
      {bootstrapState === 'empty' && (
        <Callout tone="neutral" title="No data">
          {bootstrapMessage || 'No Tax Configuration data was returned by the backend for this selection.'}
        </Callout>
      )}

      {!isUnauthorized && hasSelection && (
        <div className="mgo-stack mgo-gap-l">
          <div className="mgo-stack mgo-gap-s">
            <div className="mgo-cluster mgo-gap-s">
              <Tag label={selectedRegion} tone="blue" />
              <Tag label={selectedFinancialYear} tone="neutral" />
            </div>
          </div>

          <TabGroup value={activeGroup} onValueChange={handleGroupChange}>
            {GROUPS.map((group) => (
              <Tab key={group.key} value={group.key} label={group.label}>
                {group.key === 'payg-scales' ? (
                  <TaxScaleMasterDetail
                    groupDetails={groupDetails}
                    groupMessage={groupMessage}
                    groupState={groupState}
                  />
                ) : (
                  <SectionedTaxConfigurationGroup
                    groupDetails={groupDetails}
                    groupKey={group.key}
                    groupLabel={group.label}
                    groupMessage={groupMessage}
                    groupState={groupState}
                  />
                )}
              </Tab>
            ))}
          </TabGroup>
        </div>
      )}
    </section>
  );
};
